import THREE from "three";
import { getCenterPoint } from "../hud/ui/positions";
import { Queue } from "./queue";
import { enumMeshVertices } from "../../wgs/scene/VertexEnumerator";

export const SystemConnectionType = Object.freeze({
  Previous: -1,
  Next: 1
});

/** MEP system */
export class System {

  constructor(id, dbId, name, filter, tolerance, isDirty = false, mapCenterLines = false) {

    // Defined by the backend
    this.id = id;
    this.dbId = dbId;

    /** @type {Map<string, Map<number, SystemElement>>} connections per model */
    this.connections = new Map();

    // User defined
    this.name = name;
    this.tolerance = tolerance;
    this.mapCenterLines = mapCenterLines;

    // Encoded filter, same as server-side
    this.filter = filter;
    // Connections get outdated due to element changes
    this.isDirty = isDirty;

    // Client-side data
    this.elementsCount = 0;
    this.subsystemAttrHash = null;
    this.subsystems = null;
  }

  clear() {
    this.elementsCount = 0;
    this.connections.clear();
  }

  /**
   * @returns {bool}
   */
  hasCachedConnections() {
    return this.connections.size > 0;
  }

  /**
   * @param {SystemElement} element
   */
  addElement(element) {
    const urn = element.model.urn();
    const dbId = element.dbId;

    if (this.hasElement(urn, dbId)) {
      return;
    }

    if (!this.connections.has(urn)) {
      this.connections.set(urn, new Map());
    }

    const perModel = this.connections.get(urn);
    perModel.set(dbId, element);
  }

  /**
   * @param {String} urn
   * @param {Number} dbId
   * @returns {SystemElement}
   */
  getElement(urn, dbId) {
    return this.connections.get(urn)?.get(dbId);
  }

  /**
   * @param {String} urn
   * @param {Number} dbId
   */
  hasElement(urn, dbId) {
    return !!this.getElement(urn, dbId);
  }

  /**
   * @param {String} urn
   * @param {Number} dbId
   */
  removeElement(urn, dbId) {
    this.getElement(urn, dbId)?.dtor();
    return this.get(urn)?.delete(dbId);
  }

  getElementSubsystem(modelAttrData, dbId) {
    if (!this.subsystemAttrHash || !this.subsystems || this.subsystems.size === 0) return null;
    if (!modelAttrData) return null;
    if (!modelAttrData[this.subsystemAttrHash]) {
      console.warn(`No subsystem attribute data found for ${this.subsystemAttrHash}`);
      return null;
    }

    const { buckets, dbId2bucketIdx } = modelAttrData[this.subsystemAttrHash];
    if (!buckets || !dbId2bucketIdx) return null;

    const bucketIdx = dbId2bucketIdx[dbId];
    if (bucketIdx === undefined) return null;

    const bucket = buckets[bucketIdx];
    if (!bucket) return null;

    const subsystemId = bucket.toString();
    if (!subsystemId) return null;

    return this.subsystems.get(subsystemId);
  }

  /**
   * Generator, iterate through all elements of the system
   */
  *allElements() {
    for (const perModel of this.connections.values()) {
      for (const element of perModel.values()) {
        yield element;
      }
    }
  }

  addSubsystem(subsystem) {
    if (!this.subsystems) {
      this.subsystems = new Map();
    }

    this.subsystems.set(subsystem.id, subsystem);
  }

  clearVisitedFlag() {
    for (const perModel of this.connections.values()) {
      for (const element of perModel.values()) {
        element.visited = false;
      }
    }
  }
}

/*
 * A collection of SystemElements that are related to each other
 * and belong to the same system.
 */
export class Subsystem {
  constructor(system, dbId, attrHash, id, name) {
    this.system = system;
    this.elements = [];

    this.attrHash = attrHash;
    this.id = id;
    this.dbId = dbId;
    this.name = name;
  }
}

const _boxArr = new Array(6);
const tmpMat = new THREE.Matrix4();
const _tmpBox1 = new THREE.Box3();
const _tmpBox2 = new THREE.Box3();

let _nextId = 1;

export class SystemElement {
  /**
   * Object that represents an element within a System.
   * @param {DtModel} model
   * @param {Number} dbId
   */
  constructor(model, dbId) {
    this.model = model;
    this.dbId = dbId;
    this.id = _nextId++;
    this.visited = false; //used during dfs traversal to detect cycles
    this.intersectionPts; //used to cache intersection points between elements

    this.previous = [];
    this.next = [];
  }

  /**
   * @param {SystemElement} conn
   * @returns {SystemConnectionType}
   */
  getConnectionType(conn) {
    if (this.previous.includes(conn)) return SystemConnectionType.Previous;else
    if (this.next.includes(conn)) return SystemConnectionType.Next;else
    return null;
  }

  /**
   * Connect SystemElement to this element.
   * Does not propagate, you should handle possible mirror connection.
   * @param {SystemElement} el
   * @param {SystemConnectionType} connType Relationship `el` has on this element (default: Next)
   * (Ex: connType === SystemConnectionType.Next) `el` is downstream from this element.
   */
  connect(el, connType = SystemConnectionType.Next) {

    // no-op
    if (connType === this.getConnectionType(el)) {
      return;
    }

    this.disconnect(el);

    // Add connection
    if (connType === SystemConnectionType.Previous) {
      this.previous.push(el);
    } else {
      this.next.push(el);
    }
  }

  /**
   * Removes SystemElement connection from this element.
   * Does not propagate, you should handle possible mirror connection.
   * @param {SystemElement} conn
   */
  disconnect(el) {
    const idx = this.previous.indexOf(el);
    if (idx !== -1) {
      this.previous.splice(idx, 1);
    }

    const idx2 = this.next.indexOf(el);
    if (idx2 !== -1) {
      this.next.splice(idx2, 1);
    }
  }

  /**
   * Remove all reference to this System Element and delete connection sets
   */
  dtor() {
    for (const conn of this.allConnections()) {
      conn.disconnect(this);
    }

    delete this.previous;
    delete this.next;
  }

  isStandalone() {
    return this.connectionCount() === 0;
  }

  isLeaf() {
    return this.connectionCount() === 1;
  }

  isFork() {
    return this.connectionCount() > 2;
  }

  connectionCount() {
    return this.previous.length + this.next.length;
  }

  /**
   * @param {boolean} inclusiveSelect
   * @returns {[SystemElement]} neighbours
   */
  getNeighbours(inclusiveSelect = false) {
    const neighbours = this.previous.concat(this.next);

    if (inclusiveSelect) {
      neighbours.push(this);
    }

    return neighbours;
  }

  *allConnections() {
    for (const conn of this.previous) {
      yield conn;
    }
    for (const conn of this.next) {
      yield conn;
    }
  }

  /**
   * Enum connected elements of given element
   * @param {SystemConnectionType?} direction Direction of travel, all known connections if unspecified
   * @param {(element: SystemElement, connectedElement: SystemElement) => {}} cb
   */
  enumConnections(direction, cb, _depth = 0) {
    if (!(cb instanceof Function)) {
      return;
    }

    _depth++;

    if (direction !== SystemConnectionType.Next) {
      for (const conn of this.previous) {
        cb(this, conn, _depth);
      }
    }

    if (direction !== SystemConnectionType.Previous) {
      for (const conn of this.next) {
        cb(this, conn, _depth);
      }
    }
  }

  getCenterPoint(dst) {
    return getCenterPoint(this.model, this.dbId, dst);
  }

  getIntersectionPoint(connection) {
    // Check if we already calculated intersection between these two elements
    if (this.intersectionPts && connection.id in this.intersectionPts) {
      return this.intersectionPts[connection.id];
    } else if (connection.intersectionPts && this.id in connection.intersectionPts) {
      return connection.intersectionPts[this.id];
    }

    this.intersectionPts ??= {};
    let pt = this._doMeshBoxIntersectionAvg(connection);

    if (pt) {
      // Cache mesh-box intersection result, if any
      this.intersectionPts[connection.id] = pt;
    } else {
      const c1 = this.getCenterPoint();
      const c2 = connection.getCenterPoint();
      if (c1 && c2) {
        pt = c1;
        pt.add(c2);
        pt.multiplyScalar(0.5);
      }
    }

    return pt;
  }

  /**
   * Determine the average position of the intersection between two elements.
   * The intersection is between the smaller element's mesh and larger element's bbox,
   * size is defined by the bbox of both elements.
   */
  _doMeshBoxIntersectionAvg(connection) {
    let element = this;
    let bbox1 = _tmpBox1;
    let bbox2 = _tmpBox2;

    // Get bboxes and use larger box
    element.model.getInstanceTree().getNodeBox(this.dbId, _boxArr);
    bbox1.min.set(_boxArr[0], _boxArr[1], _boxArr[2]);
    bbox1.max.set(_boxArr[3], _boxArr[4], _boxArr[5]);

    connection.model.getInstanceTree().getNodeBox(connection.dbId, _boxArr);
    bbox2.min.set(_boxArr[0], _boxArr[1], _boxArr[2]);
    bbox2.max.set(_boxArr[3], _boxArr[4], _boxArr[5]);

    const bbox1Size = bbox1.size().lengthSq();
    const bbox2Size = bbox2.size().lengthSq();

    // Use the bigger bbox
    if (bbox2Size > bbox1Size) {
      bbox1 = bbox2;
    } else {
      element = connection;
    }

    // TODO: find something better
    bbox1.expandByScalar(1);

    // Get start-mesh
    const fl = element.model.getFragmentList();
    let fragIds = fl.fragments.dbId2fragId[element.dbId];
    fragIds = Array.isArray(fragIds) ? fragIds : [fragIds];

    const intersectingAvg = new THREE.Vector3(0, 0, 0);
    let intersectingPointCount = 0;

    for (const fragId of fragIds) {
      const geom = fl.getGeometry(fragId);
      if (!geom) continue;
      fl.getWorldMatrix(fragId, tmpMat);
      enumMeshVertices(geom, (p) => {
        if (bbox1.containsPoint(p)) {
          intersectingAvg.add(p);
          intersectingPointCount++;
        }
      }, tmpMat);
    }

    if (intersectingPointCount === 0) {
      return null;
    }

    intersectingAvg.divideScalar(intersectingPointCount);

    return intersectingAvg;
  }


  /**
   * Performs a depth-first search starting at this node. Criterion for termination is
   * implemented by the given callback function, which takes the current node and the current path
   * through the graph (which includes the current node as its last entry) as parameters.
   * @param {function} cb - callback function which returns true to terminate the search.
   */
  dfs(cb) {

    let path = [];
    let stack = [this];

    while (stack.length > 0) {
      let current = stack[stack.length - 1];

      if (current.visited) {
        stack.pop();
        current.visited = false;
        path.pop();
        continue;
      }

      path.push(current);
      current.visited = true;

      if (cb(current, path)) {
        for (const e of stack) {
          e.visited = false;
        }
        return;
      }

      for (let neighbor of current.previous) {
        if (!neighbor.visited) {
          stack.push(neighbor);
        }
      }
      for (let neighbor of current.next) {
        if (!neighbor.visited) {
          stack.push(neighbor);
        }
      }
    }
  }

  /**
   * Callback for bfs()
   * Return true to stop search beyond current node
   * @callback bfsCB
   * @param {SystemElement} node the currently traversed node
   * @param {SystemElement[]} path the path to taken to reach the current node (including that node)
   * @returns {Number} Falsy to continue traversal, 1 to stop the search, any other value to skip further traversal from current node only
   */

  /**
   * Breadth first search traversal of a sub-system starting from this node.
   *
   * @param {bfsCB} cb Receives current node and optionally path traversed to the current node.
   *                      Determines whether to stop traversal from current node or end the bfs completely
   *                      Return falsy to continue traversal, 1 to stop the search and anything else to stop traversal of current subtree
   * @param {Boolean} wantFullPaths optionally construct an array with the path taken to reach the current node
   */
  bfs(cb, wantFullPaths) {

    const queue = new Queue();
    queue.enqueue(wantFullPaths ? [this] : this);
    const visited = new Set();
    visited.add(this);

    const res = cb(this, wantFullPaths ? [this] : undefined);
    if (res) {
      return;
    }

    while (queue.getSize() > 0) {
      const path = queue.dequeue();
      const curr = wantFullPaths ? path[path.length - 1] : path;

      for (const next of curr.allConnections()) {

        if (visited.has(next)) continue;
        visited.add(next);

        const npath = wantFullPaths ? [...path, next] : undefined;
        const res = cb(next, npath);
        if (!res) {
          queue.enqueue(wantFullPaths ? npath : next);
        } else if (res === 1) {
          return;
        }
      }
    }
  }


  /**
   * Returns all paths from this system element to each of the target end elements
   * The paths are arrays of elements, sorted by length (number of elements traversed)
   * @param {Set} endElements
   * @returns {Map<SystemElement, SystemElement[][]>}
   */
  traceAllPathsTo(endElements) {

    const paths = [];
    let mPoints = 0;
    let tooManyPaths = false;
    let foundEndElements = new Set();

    //find all paths of interest
    const t0 = performance.now();
    this.dfs((current, path) => {
      if (endElements.has(current)) {
        paths.push(path.slice());
        foundEndElements.add(current);
      }

      mPoints += path.length;
      if (mPoints > 1e8) {
        mPoints = 0;
        const t1 = performance.now();
        if (t1 - t0 > 500) {
          console.warn("dfs taking too long -- bailing");
          tooManyPaths = true;
          return true;
        }
      }
    });

    if (tooManyPaths) {
      foundEndElements.clear();

      paths.length = 0;
      this.bfs((current, path) => {
        if (endElements.has(current)) {
          paths.push(path.slice());
          foundEndElements.add(current);
          if (foundEndElements.size === endElements.size) {
            return 1;
          }
        }
      }, true);
    }

    //group path by destination element and collect shortest path(s) indices
    const perEndElement = new Map();
    for (const path of paths) {
      const endElement = path[path.length - 1];

      let value = perEndElement.get(endElement);
      if (!value) {
        value = { list: [path], shortest: [0] };
        perEndElement.set(endElement, value);
      } else {
        const { list, shortest } = value;
        list.push(path);

        // Collect indices of the shortest paths
        if (path.length <= list[shortest[0]].length) {
          if (path.length < list[shortest[0]].length) {
            // Reset shortest paths
            shortest.length = 0;
          }

          shortest.push(list.length - 1);
        }
      }
    }

    // Move the shortest paths to the front of each list of paths
    let tmpPath;
    for (const [endElement, value] of perEndElement) {
      const { list, shortest } = value;
      for (let targetIdx = 0; targetIdx < shortest.length; targetIdx++) {
        const swapIdx = shortest[targetIdx];
        tmpPath = list[swapIdx];
        list[swapIdx] = list[targetIdx];
        list[targetIdx] = tmpPath;
      }

      // Point to the list of paths
      perEndElement.set(endElement, list);
    }

    return perEndElement;
  }
}